// LotWise Home — quote-led pricing data
// priceLabel: shown small above the price ("Starting at" / "Typical range" / null)
// price: display value ("$395", "$595 – $895", "Custom")
// scope: representative deliverables ("Typical scope may include")
// quoteNote: how the final quote is determined

export const singleListingPlans = [
  {
    id: "listing-essential",
    name: "Listing Essential",
    priceLabel: "Starting at",
    price: "$395",
    unit: "per listing",
    recommended: false,
    intro:
      "For agents who need a property photographed and organized properly without overbuilding the listing package.",
    includes: [
      "Property photography",
      "Exterior and interior room capture",
      "Basic feature notes",
      "MLS-ready photo exports",
      "Web-ready photo exports",
      "Organized delivery folder",
      "Listing-readiness checklist",
    ],
    bestFor:
      "Condos, smaller homes, individual listings, newer agents, and agents who need a clean professional listing foundation.",
    quoteNote:
      "Final pricing is confirmed before booking and may vary based on property size, location, complexity, and required deliverables.",
    payment: "Paid in full at booking unless otherwise approved.",
    cta: "Request Listing Essential Quote",
  },
  {
    id: "listing-standard",
    name: "Listing Standard",
    priceLabel: "Typical range",
    price: "$595 – $895",
    unit: "per listing",
    recommended: true,
    intro:
      "For agents who want a stronger listing package with better documentation, cleaner presentation support, and more complete listing readiness.",
    includes: [
      "Everything in Listing Essential",
      "Expanded room and feature documentation",
      "Curb appeal and exterior feature capture",
      "Property highlight notes for agent review",
      "Social-ready image selections",
      "Website listing support materials",
      "Listing presentation review checklist",
    ],
    bestFor:
      "Agents who want a more polished listing standard and stronger seller-facing confidence.",
    quoteNote:
      "Final pricing is quoted based on property size, location, scope, turnaround needs, and selected deliverables.",
    payment:
      "50% due at booking. Remaining 50% due before final asset delivery or go-live support.",
    cta: "Request Listing Standard Quote",
  },
  {
    id: "listing-plus",
    name: "Listing Plus",
    priceLabel: "Typical range",
    price: "$895 – $1,495+",
    unit: "per listing",
    recommended: false,
    intro:
      "For agents who want a more complete listing support package for higher-value properties, competitive listings, rural properties, larger homes, or seller relationships where presentation needs to feel especially controlled.",
    includes: [
      "Everything in Listing Standard",
      "Larger photo set",
      "More detailed feature capture",
      "Seller-facing listing prep notes",
      "Print-friendly photo selections",
      "Enhanced website listing support materials",
      "One round of post-delivery listing support adjustments",
      "Optional bundled add-ons where appropriate",
    ],
    bestFor:
      "Higher-value listings, larger homes, competitive properties, established agents, and listings where the seller experience matters heavily.",
    quoteNote:
      "Final pricing is quoted based on property size, location, complexity, media requirements, and support scope.",
    payment:
      "50% due at booking. Remaining 50% due before final asset delivery or go-live support.",
    cta: "Request Listing Plus Quote",
  },
  {
    id: "media-walkthrough",
    name: "Media Walkthrough Package",
    priceLabel: "Typical range",
    price: "$995 – $2,500+",
    unit: "per listing",
    recommended: false,
    intro:
      "For listings that need a stronger buyer-facing experience through video, AI walkthroughs, drone capture, guided property media, or a more complete visual package.",
    includes: [
      "Video walkthrough",
      "AI walkthrough",
      "Drone exterior capture where available",
      "Guided property sequence",
      "Social-ready video cutdowns",
      "Feature-focused visual capture",
      "Website-ready media assets",
      "MLS-supporting photo and media exports where appropriate",
    ],
    bestFor:
      "Premium listings, larger homes, rural properties, distinctive properties, seller-sensitive listings, and agents who want a more complete presentation package.",
    quoteNote:
      "Every Media Walkthrough Package is quoted based on the property, location, flight requirements, media scope, editing requirements, turnaround timeline, and intended use.",
    payment:
      "50% due at booking. Remaining 50% due before final asset delivery.",
    cta: "Request Media Walkthrough Quote",
  },
];

export const recurringPlans = [
  {
    id: "active-agent",
    name: "Active Agent Plan",
    priceLabel: "Starting at",
    price: "$1,295",
    unit: "per month",
    recommended: true,
    intro:
      "For agents with consistent listing flow who want a repeatable listing support system.",
    includes: [
      "Recurring listing support",
      "Preferred scheduling",
      "Listing Standard-level support for included visits",
      "Property photos and listing detail capture",
      "Room and feature documentation",
      "Organized monthly listing asset library",
      "Website listing support materials",
      "Listing-readiness checklist for each property",
      "Preferred rates for additional listings and add-ons",
    ],
    bestFor:
      "Agents who regularly bring listings to market and want a more controlled process.",
    quoteNote:
      "Monthly plan pricing is quoted based on expected listing volume, service area, property types, support needs, and add-on requirements.",
    payment: "Paid at the start of each monthly billing period.",
    note:
      "Included visits do not roll over unless approved in writing. Larger or complex properties may require a custom quote.",
    cta: "Request Active Agent Quote",
  },
  {
    id: "team-standard",
    name: "Team Standard",
    priceLabel: "Starting at",
    price: "$2,795",
    unit: "per month",
    recommended: false,
    intro:
      "For real estate teams that need a cleaner listing standard across multiple agents and multiple properties.",
    includes: [
      "Recurring team listing support",
      "Team-wide listing capture standard",
      "Property photo and detail capture",
      "Room and feature documentation",
      "Organized listing asset library",
      "Website and brokerage listing support materials",
      "Social-ready image selections",
      "Listing-readiness checklist for each property",
      "Priority scheduling",
      "Monthly listing standard review",
      "Preferred rates for additional listings and add-ons",
    ],
    bestFor:
      "Teams with recurring listing volume who want consistency, speed, and less coordination drag.",
    quoteNote:
      "Team pricing is quoted based on expected listing volume, number of agents, property types, service area, and required workflow support.",
    payment: "Paid at the start of each monthly billing period.",
    note:
      "Included visits do not roll over unless approved in writing. Larger or complex properties may require a custom quote.",
    cta: "Request Team Quote",
  },
  {
    id: "brokerage-agency",
    name: "Brokerage / Agency Plan",
    priceLabel: null,
    price: "Custom",
    unit: "pricing",
    recommended: false,
    intro:
      "For brokerages, agencies, and high-volume teams that need a preferred listing execution partner.",
    includes: [
      "Custom listing visit volume",
      "Brokerage-wide listing standard",
      "Agent support workflow",
      "Preferred scheduling process",
      "Property media capture",
      "Listing detail capture",
      "Website and brokerage listing support materials",
      "Listing asset organization",
      "Team onboarding",
      "Custom monthly reporting if required",
      "Optional AI walkthrough, video, drone, and premium media workflows",
    ],
    bestFor:
      "Brokerages and agencies that want a consistent listing presentation system across multiple agents or offices.",
    quoteNote:
      "Custom plans are quoted based on listing volume, locations, workflow needs, media requirements, and support scope.",
    payment:
      "Custom plans are quoted based on listing volume, locations, workflow needs, and support scope. First payment is required before work begins.",
    cta: "Request Brokerage Quote",
  },
];

export const addOns = [
  { label: "Additional listing visit", price: "from $425" },
  { label: "Rush turnaround", price: "from $150" },
  { label: "Travel beyond core service radius", price: "quoted or mileage-based" },
  { label: "Drone exterior capture", price: "from $195" },
  { label: "Video walkthrough", price: "quoted" },
  { label: "AI walkthrough", price: "quoted" },
  { label: "Premium walkthrough media package", price: "quoted" },
  { label: "Twilight exterior set", price: "from $195" },
  { label: "Floor plan coordination", price: "from $195" },
  { label: "Social listing image pack", price: "from $125" },
  { label: "Social video cutdowns", price: "quoted" },
  { label: "Feature sheet support materials", price: "from $175" },
  { label: "Website listing upload support", price: "from $150" },
  { label: "Larger property surcharge, 3,500+ sq. ft.", price: "quoted" },
  { label: "Rural property or acreage quote", price: "quoted" },
  { label: "Multi-property same-day scheduling", price: "quoted" },
  { label: "Brokerage workflow support", price: "quoted" },
];
